/**
 * Builder model — the pure layer behind ReportBuilderPage.
 *
 * Holds the builder's data model (`Step` and friends) and the compile functions
 * that translate a `Step` to/from the shared `ViewConfig`. No React here, so this
 * is unit-testable in isolation (see builder-model.test.ts for the round-trip test).
 */
import type { FrameDef, SourceId } from './frames'
import type { FrameId } from './pipeline-types'
import type { Filter, ViewConfig } from './view-config'

// ── types ─────────────────────────────────────────────────────────────────────

export type Panel = 'limit' | 'columns' | 'goal' | 'summary-row' | null
export type AggKind = 'group-by' | 'summary' | 'pivot'
export type MetricAgg = 'count' | 'sum' | 'avg'
export type MetricDef = { agg: MetricAgg; field: string | null }

export type Step = {
  id: string
  aggKind: AggKind | null
  groupBy: string | null
  seriesBy: string | null
  metrics: MetricDef[]
  filters: Filter[]
  sort: { by: string; dir: 'asc' | 'desc' } | null
  limit: number
  columns: string[]
  panel: Panel
  goal: { value: number; kpiId?: string; dir: 'above' | 'below' } | null
  summaryFn: 'sum' | 'avg' | null
}

export type Adder = {
  afterId: string
  aggKind: AggKind
  groupBy: string
  seriesBy: string
  metrics: MetricDef[]
}

// ── helpers ───────────────────────────────────────────────────────────────────

let _seq = 0
const uid = () => `rb-${++_seq}`

export function makeStep(frame: FrameDef): Step {
  return {
    id: uid(),
    aggKind: null,
    groupBy: null,
    seriesBy: null,
    metrics: [{ agg: 'count', field: null }],
    filters: [],
    sort: null,
    limit: 0,
    columns: frame.defaultColumns,
    panel: null,
    goal: null,
    summaryFn: null,
  }
}

export function metricLabel(
  agg: MetricAgg,
  field: string | null,
  frame: FrameDef
): string {
  if (agg === 'count') return 'Count'
  const f = field ? (frame.labels[field] ?? field) : '?'
  return agg === 'sum' ? `Sum of ${f}` : `Avg of ${f}`
}

export function stepLabel(step: Step, frame: FrameDef): string {
  if (!step.aggKind) return `All ${frame.label}`
  if (step.aggKind === 'summary') return `${frame.label} Summary`
  const row = frame.labels[step.groupBy!] ?? step.groupBy
  if (step.aggKind === 'pivot') {
    const col = frame.labels[step.seriesBy!] ?? step.seriesBy
    return `${frame.label} by ${row} × ${col}`
  }
  return `${frame.label} by ${row}`
}

/**
 * Filters inherited by a downstream step — the filter cascade. A step at `index`
 * runs against the base source narrowed by every filter defined on the steps
 * before it, so editing an upstream filter changes all downstream results.
 * (Naive concat: same-field filters across steps are ANDed by the engine, not
 * merged — fine for the prototype.)
 */
export function upstreamFilters(steps: Step[], index: number): Filter[] {
  return steps.slice(0, Math.max(0, index)).flatMap((s) => s.filters)
}

export function toConfig(
  step: Step,
  frame: FrameDef,
  sources: SourceId[],
  extraFilters: Filter[] = []
): ViewConfig {
  // Nearest registered FrameId — a fallback for readers that predate `sources`.
  // `sources` (persisted below) is the real source of truth at query time; a
  // multi-source combo can't be expressed as one FrameId (that's the join-drop bug).
  const configFrameId: FrameId = sources.includes('activity')
    ? 'deal_activity'
    : sources.includes('opportunities')
      ? 'deal_opportunity'
      : 'deal'

  // Prepend inherited upstream filters ahead of the step's own.
  const filters = extraFilters.length
    ? [...extraFilters, ...step.filters]
    : step.filters

  const configMetrics = step.metrics.map((m) => ({
    agg: m.agg,
    field: m.field ?? undefined,
    label: metricLabel(m.agg, m.field, frame),
  }))
  const threshold = step.goal
    ? { value: step.goal.value, dir: step.goal.dir, kpiId: step.goal.kpiId }
    : undefined

  if (!step.aggKind) {
    return {
      id: step.id,
      name: stepLabel(step, frame),
      description: '',
      kind: 'list',
      frame: configFrameId,
      sources,
      filters,
      columns: step.columns,
      sort: step.sort ?? undefined,
      limit: step.limit,
      chart: 'table-only',
      knobs: [],
      threshold,
      listSummaryFn: step.summaryFn ?? undefined,
    }
  }
  return {
    id: step.id,
    name: stepLabel(step, frame),
    description: '',
    kind: 'aggregate',
    frame: configFrameId,
    sources,
    filters,
    groupBy: step.groupBy
      ? {
          field: step.groupBy,
          bucket: frame.dateFields.includes(step.groupBy) ? 'month' : undefined,
        }
      : undefined,
    seriesBy: step.seriesBy ?? undefined,
    metrics: configMetrics,
    sort: step.sort ?? undefined,
    limit: step.limit,
    chart: 'table-only',
    knobs: [],
    threshold,
    // On a grouped table this flags the grand-total footer (see ReportTable);
    // 'sum' = on (set via the Σ Total toggle).
    listSummaryFn: step.summaryFn ?? undefined,
  }
}

export function connectorLabel(step: Step, frame: FrameDef): string {
  const m = step.metrics
    .map((x) => metricLabel(x.agg, x.field, frame))
    .join(', ')
  if (step.aggKind === 'summary') return `Summary · ${m}`
  const row = frame.labels[step.groupBy!] ?? step.groupBy
  if (step.aggKind === 'pivot') {
    const col = frame.labels[step.seriesBy!] ?? step.seriesBy
    return `${row} × ${col} · ${m}`
  }
  return `${row} · ${m}`
}

// ── Edit helpers ──────────────────────────────────────────────────────────────

export function sourcesFromFrameId(frameId: FrameId): SourceId[] {
  if (frameId === 'deal_activity') return ['deals', 'activity']
  if (frameId === 'deal_opportunity') return ['deals', 'opportunities']
  return ['deals']
}

export function stepFromConfig(config: ViewConfig, frame: FrameDef): Step {
  const aggKind: AggKind | null =
    config.kind === 'list'
      ? null
      : !config.groupBy
        ? 'summary'
        : config.seriesBy
          ? 'pivot'
          : 'group-by'
  return {
    id: uid(),
    aggKind,
    groupBy: config.groupBy?.field ?? null,
    seriesBy: config.seriesBy ?? null,
    metrics: config.metrics?.map((m) => ({
      agg: (m.agg === 'sum' || m.agg === 'avg' ? m.agg : 'count') as MetricAgg,
      field: m.field ?? null,
    })) ?? [{ agg: 'count', field: null }],
    filters: config.filters ?? [],
    sort: config.sort ?? null,
    limit: config.limit ?? 0,
    columns: config.columns ?? frame.defaultColumns,
    panel: null,
    goal: config.threshold
      ? {
          value: config.threshold.value,
          dir: config.threshold.dir,
          kpiId: config.threshold.kpiId,
        }
      : null,
    summaryFn: config.listSummaryFn ?? null,
  }
}
